/*
  assets/littleg-city-result-storage.js — FAMILY-AWARE-RESULT-STORAGE-07
  Updated: 2026-09-03

  Owner:
  - Local LittleG result persistence, scoped deletion and lead-exit payload normalization.
  - Provides window.LittleGResultStorage for mission cards, result history, generator previews,
    handoff validation, localized result saving, JSON export and local viral-loop surfaces.

  Boundary:
  - Storage API only: no Result Card DOM hydration, click, share, copy, aria or visual ownership.
  - Storage keys and list primitives remain private to this file.
  - No formal report, government filing, tender publication, GFun ledger, Shopify order,
    customer metafield, backend API, AVM, Repo B or official valuation claim.
  - Records remain local, private and time-limited.
*/
(function () {
  'use strict';

  var VERSION = 'FAMILY-AWARE-RESULT-STORAGE-07';

  var DEFAULT_EXPIRY_HOURS = 72;
  var FAMILY_EXPIRY_HOURS = 365 * 24;
  var CHECKIN_EXPIRY_HOURS = 365 * 24;

  var DEFAULT_MAX_HISTORY = 20;
  var FAMILY_MAX_HISTORY = 100;
  var CHECKIN_MAX_HISTORY = 365;

  var CATEGORY_BY_DAILY_SCENE = {
    'green-man-crossing': 'crossing_safety',
    'market-shop-front-blocked': 'street_walkability',
    'estate-recycling-room': 'recycling_clarity',
    'rainwater-mosquito-spot': 'after_rain_safety',
    'public-toilet-cleanliness': 'public_facility',
    'commute-gate-crowd': 'commute_flow',
    'elderly-ramp-handrail': 'elder_friendly_access',
    'building-leakage-ac-drip': 'building_leakage',
    'park-playground-pet-zone': 'park_facility',
    'extreme-weather-home-route': 'extreme_weather_route',
    'trade-up-g': 'property_value_snapshot',
    'old-building-g': 'old_building_value_snapshot',
    'mortgage-g': 'mortgage_gap_snapshot'
  };

  var VALUATION_META = {
    'trade-up-g': {
      valuation_type: 'trade_up',
      property_purpose: 'trade_up_precheck',
      value_band: 'Near market range / more data required',
      risk_level: 'Medium',
      confidence_level: 'Medium',
      risk_hint: 'Nearby transactions, area definitions, building age and condition may affect the informal value review.',
      service_candidate_hint: 'trade-up-precheck',
      service_lane: 'property-assets-investment'
    },
    'old-building-g': {
      valuation_type: 'old_building',
      property_purpose: 'urban_renewal_value_precheck',
      value_band: 'Insufficient data / current condition required',
      risk_level: 'Medium to high',
      confidence_level: 'Low to medium',
      risk_hint: 'Building age, maintenance, ownership, ancillary items and improvement potential may materially affect the review.',
      service_candidate_hint: 'old-building-precheck',
      service_lane: 'urban-renewal'
    },
    'mortgage-g': {
      valuation_type: 'mortgage_gap',
      property_purpose: 'mortgage_risk_precheck',
      value_band: 'Conservative / lender valuation requires confirmation',
      risk_level: 'Medium to high',
      confidence_level: 'Low to medium',
      risk_hint: 'Lender valuation, transaction price, building age, property type and loan-to-value ratio may create a funding gap.',
      service_candidate_hint: 'mortgage-risk-precheck',
      service_lane: 'property-assets-investment'
    }
  };

  var LEAD_EXIT_RULES = {
    city_issue: { destination: 'gcitypartners', path: '/pages/gcitypartners', intent: 'city-issue-review', label: 'Send to GCityPartners' },
    gcitypartners: { destination: 'gcitypartners', path: '/pages/gcitypartners', intent: 'gcitypartners-entry', label: 'Send to GCityPartners' },
    valuation_snapshot: { destination: 'cards', path: '/pages/result-history', intent: 'valuation-snapshot-local', label: 'Save value snapshot' },
    property_value_precheck: { destination: 'gcitypartners', path: '/pages/gcitypartners', intent: 'service-candidate', label: 'Ask GLG to follow up' },
    gproperty: { destination: 'gproperty', path: '/pages/gproperty', intent: 'property-lead', label: 'Send to GProperty' },
    gtender: { destination: 'gtender', path: '/pages/gtender', intent: 'tender-lead', label: 'Send to GTender' },
    partner_opportunity: { destination: 'gtender', path: '/pages/gtender', intent: 'partner-opportunity-candidate', label: 'Create city opportunity card', launch_gate: 'patch18_dependency_gated' },
    corporate_site: { destination: 'corporate_site', path: 'https://glkgjt.com', intent: 'corporate-lead', label: 'Open corporate entry' }
  };

  LEAD_EXIT_RULES.corporate = LEAD_EXIT_RULES.corporate_site;

  var FALLBACK_RULE = {
    destination: 'cards',
    path: '/pages/result-history',
    intent: 'unknown-lead-type',
    label: 'Back to card center'
  };

  var STORAGE_KEYS = Object.freeze({
    history: 'littleg_offline_city_missions',
    lastResult: 'littleg.cityMission.lastResult',
    generatorHistory: 'littleg_result_history',
    drgHistory: 'drg_city_mission_history_v1'
  });

  var LIST_KEYS = Object.freeze([
    STORAGE_KEYS.history,
    STORAGE_KEYS.generatorHistory,
    STORAGE_KEYS.drgHistory
  ]);

  function now() {
    return new Date().toISOString();
  }

  function uid() {
    return 'littleg-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
  }

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function key(value) {
    return clean(value).toLowerCase().replace(/-/g, '_').replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  }

  function localizedArray(value) {
    var list = [];
    if (!value) return list;
    if (Array.isArray(value)) list = value;
    else if (Array.isArray(value.clues)) list = value.clues;
    else if (Array.isArray(value.items)) list = value.items;
    else if (typeof value === 'string') list = value.split(/[|,]/);
    return list.map(clean).filter(Boolean).slice(0, 20);
  }

  function dailyId(payload) {
    payload = payload || {};
    return clean(payload.story_id || payload.round_key || payload.daily_scene_id || payload.g_id || payload.g_type_id);
  }

  function valuationPreset(payload) {
    return VALUATION_META[dailyId(payload)] || null;
  }

  function isValuationPayload(payload) {
    payload = payload || {};
    return !!(
      valuationPreset(payload) ||
      clean(payload.g_type) === 'valuation' ||
      clean(payload.card_type || payload.cardType) === 'valuation-discovery-card' ||
      clean(payload.summary_type || payload.summaryType) === 'informal_valuation_snapshot' ||
      clean(payload.valuation_type) ||
      clean(payload.value_band) ||
      clean(payload.service_candidate_hint)
    );
  }

  function normalizeLeadType(value) {
    return key(value || 'city_issue') || 'city_issue';
  }

  function resolveExitRule(type, destination) {
    var normalizedType = normalizeLeadType(type);
    var normalizedDestination = normalizeLeadType(destination);
    var rule = LEAD_EXIT_RULES[normalizedType] || LEAD_EXIT_RULES[normalizedDestination] || FALLBACK_RULE;
    var leadType = LEAD_EXIT_RULES[normalizedType]
      ? normalizedType
      : (LEAD_EXIT_RULES[normalizedDestination] ? normalizedDestination : (normalizedType || 'unknown'));
    return Object.assign({ lead_type: leadType }, rule);
  }

  function normalizeNextDestination(type, destination) {
    return resolveExitRule(type, destination).destination || 'cards';
  }

  function category(payload) {
    var dId = dailyId(payload);
    return clean(payload.lead_category) || CATEGORY_BY_DAILY_SCENE[dId] || clean(payload.service_lane) || 'city_issue_general';
  }

  function field(payload, name, fallback) {
    return clean(payload && payload[name]) || fallback || '';
  }

  function opportunityStatus(payload, type) {
    return clean(payload.opportunity_status || payload.opportunityStatus) ||
      (type === 'partner_opportunity' ? 'candidate' : 'not_applicable');
  }

  function visibility(payload) {
    return clean(payload.visibility || payload.record_visibility) || 'private';
  }

  function retentionPath(value) {
    if (typeof value === 'string') return clean(value);
    return clean(value && (value.storage_path || value.path || value.record_type || value.source));
  }

  function isFamilyRetention(value) {
    var path = retentionPath(value).toLowerCase();
    return (
      path === 'family' ||
      path === 'family-notice' ||
      path === 'notice' ||
      path.indexOf('family-') === 0 ||
      path.indexOf('family_') === 0 ||
      clean(value && value.source).toLowerCase() === 'littleg-family-notice'
    );
  }

  function isCheckinRetention(value) {
    return retentionPath(value).toLowerCase() === 'check-in';
  }

  function isCityCard(entry) {
    if (!entry || typeof entry !== 'object' || isFamilyRetention(entry) || isValuationPayload(entry)) return false;
    var path = retentionPath(entry).toLowerCase();
    var lead = normalizeLeadType(entry.lead_type);
    var source = clean(entry.source).toLowerCase();
    return path === 'check-in' || lead === 'city_observation_game' ||
      (path === 'mission' && source === 'littleg-game-lobby');
  }

  function retentionHours(value) {
    if (isCheckinRetention(value)) return CHECKIN_EXPIRY_HOURS;
    return isFamilyRetention(value) ? FAMILY_EXPIRY_HOURS : DEFAULT_EXPIRY_HOURS;
  }

  function expiryIso(value) {
    return new Date(Date.now() + retentionHours(value) * 3600000).toISOString();
  }

  function retentionTime(entry) {
    var raw = clean(entry && (entry.confirmed_at || entry.saved_at || entry.created_at || entry.generated_at));
    var date = raw ? new Date(raw) : null;
    return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
  }

  function isExpired(entry) {
    if (!entry) return false;
    if (isCheckinRetention(entry)) {
      var checkinTime = retentionTime(entry);
      if (checkinTime) return (checkinTime + CHECKIN_EXPIRY_HOURS * 3600000) < Date.now();
    }
    if (isFamilyRetention(entry)) {
      var baseTime = retentionTime(entry);
      if (baseTime) return (baseTime + FAMILY_EXPIRY_HOURS * 3600000) < Date.now();
    }
    if (!entry.expires_at) return false;
    var expiry = new Date(entry.expires_at);
    return !Number.isNaN(expiry.getTime()) && expiry.getTime() < Date.now();
  }

  function sortByRetentionTime(left, right) {
    return retentionTime(right) - retentionTime(left);
  }

  function pruneHistory(list) {
    var checkins = [];
    var family = [];
    var standard = [];

    (list || []).forEach(function (entry) {
      if (isCheckinRetention(entry)) checkins.push(entry);
      else if (isFamilyRetention(entry)) family.push(entry);
      else standard.push(entry);
    });

    checkins.sort(sortByRetentionTime);
    family.sort(sortByRetentionTime);
    standard.sort(sortByRetentionTime);

    return checkins.slice(0, CHECKIN_MAX_HISTORY)
      .concat(family.slice(0, FAMILY_MAX_HISTORY))
      .concat(standard.slice(0, DEFAULT_MAX_HISTORY))
      .sort(sortByRetentionTime);
  }

  function resolveServiceLane(rawLane, valuation) {
    var raw = clean(rawLane);
    if (window.GCityPartnersServiceLaneMap && typeof window.GCityPartnersServiceLaneMap.canonical === 'function') {
      var canonical = window.GCityPartnersServiceLaneMap.canonical(raw);
      if (canonical) return canonical;
    }
    return raw || (valuation ? 'property-assets-investment' : 'urban-renewal');
  }

  function normalizeLeadRecord(payload, path) {
    payload = payload && typeof payload === 'object' ? payload : {};
    var preset = valuationPreset(payload) || {};
    var valuation = isValuationPayload(payload);
    var id = clean(payload.record_id || payload.result_id || payload.card_id || payload.cardId || payload.id) || uid();
    var type = normalizeLeadType(payload.lead_type || (valuation ? 'valuation_snapshot' : 'city_issue'));
    var destination = normalizeNextDestination(type, payload.next_destination);
    var createdAt = clean(payload.created_at || payload.generated_at || payload.saved_at || payload.savedAt) || now();
    var status = clean(payload.status) || 'local_saved';
    var formalStatus = clean(payload.formal_submission_status) || 'not_submitted';
    var serviceLane = resolveServiceLane(payload.service_lane || payload.scene || payload.scenario || preset.service_lane, valuation);

    var normalized = Object.assign({}, payload, {
      schema_version: clean(payload.schema_version) || (valuation ? 'littleg_valuation_snapshot_v1' : 'littleg_lead_record_v1'),
      result_id: id,
      record_id: id,
      card_id: clean(payload.card_id || payload.cardId) || id,
      story_id: clean(payload.story_id || payload.round_key) || dailyId(payload),
      round_key: clean(payload.round_key || payload.story_id) || dailyId(payload),
      lead_type: type,
      lead_category: category(payload),
      city_pack_id: clean(payload.city_pack_id) || 'hk',
      city: clean(payload.city) || 'hong-kong',
      district: clean(payload.district || payload.district_label) || 'hong-kong',
      locale: clean(payload.locale) || 'en',
      daily_scene_id: clean(payload.daily_scene_id) || dailyId(payload),
      service_lane: serviceLane,
      hero: clean(payload.hero || payload.hero_type) || 'Today G',
      location: clean(payload.location || payload.place_label) || 'Hong Kong',
      time: clean(payload.time) || 'Available today',
      lifePain: clean(payload.lifePain || payload.life_pain) || 'An everyday urban detail was observed.',
      cityPain: clean(payload.cityPain || payload.city_pain) || 'The observation may indicate a small improvement opportunity.',
      renewalClue: clean(payload.renewalClue || payload.renewal_clue) || 'Organise the clues into an informal preliminary summary.',
      clues: localizedArray(payload.clues || payload.clue_labels || payload.daily_scene_clues),
      status: status,
      local_save_status: status,
      formal_submission_status: formalStatus,
      next_destination: destination,
      opportunity_status: opportunityStatus(payload, type),
      visibility: visibility(payload),
      source: clean(payload.source) || 'littleg',
      created_at: createdAt,
      submitted_at: clean(payload.submitted_at) || '',
      storage_path: path || payload.storage_path || 'mission'
    });

    if (valuation) {
      normalized.g_type = 'valuation';
      normalized.card_type = clean(payload.card_type || payload.cardType) || 'valuation-discovery-card';
      normalized.summary_type = clean(payload.summary_type || payload.summaryType) || 'informal_valuation_snapshot';
      normalized.valuation_type = clean(payload.valuation_type) || preset.valuation_type || 'value_snapshot';
      normalized.property_purpose = clean(payload.property_purpose) || preset.property_purpose || 'property_value_precheck';
      normalized.value_band = clean(payload.value_band) || preset.value_band || 'Insufficient data';
      normalized.risk_level = clean(payload.risk_level) || preset.risk_level || 'Medium';
      normalized.confidence_level = clean(payload.confidence_level) || preset.confidence_level || 'Low to medium';
      normalized.risk_hint = clean(payload.risk_hint) || preset.risk_hint || 'More address, area, age, use and market-comparison data is required.';
      normalized.service_candidate_hint = clean(payload.service_candidate_hint) || preset.service_candidate_hint || 'property-value-precheck';
      normalized.formal_valuation = false;
      normalized.formal_report = false;
      normalized.official_submission = false;
      normalized.checkout_enabled = false;
      normalized.formal_delivery = false;
      normalized.avm_api_status = clean(payload.avm_api_status) || 'not_connected';
      normalized.repo_b_api_status = clean(payload.repo_b_api_status) || 'not_connected';
      normalized.backend_submission_status = clean(payload.backend_submission_status) || 'not_submitted';
      normalized.delivery_status = clean(payload.delivery_status) || 'informal_snapshot_only';
      normalized.boundary = clean(payload.boundary) || 'This LittleG value snapshot is a local informal preliminary summary only. It is not a formal valuation, lender valuation, mortgage approval, tax opinion or professional report.';
    }

    return normalized;
  }

  function buildLeadExitUrl(payload) {
    payload = payload && typeof payload === 'object' ? normalizeLeadRecord(payload, payload.storage_path || 'mission') : {};
    var rule = resolveExitRule(payload.lead_type, payload.next_destination);
    var base = rule.path || FALLBACK_RULE.path;
    var external = /^https?:\/\//i.test(base);
    var url;
    try { url = new URL(base, window.location.origin); }
    catch (_) { url = new URL(FALLBACK_RULE.path, window.location.origin); }

    url.searchParams.set('source', field(payload, 'source', 'littleg'));
    url.searchParams.set('intent', rule.intent || 'lead-exit');
    url.searchParams.set('lead_type', normalizeLeadType(payload.lead_type || rule.lead_type || 'city_issue'));
    url.searchParams.set('next_destination', rule.destination || 'cards');
    url.searchParams.set('status', field(payload, 'status', 'local_saved'));
    url.searchParams.set('formal_submission_status', field(payload, 'formal_submission_status', 'not_submitted'));
    url.searchParams.set('city_pack_id', field(payload, 'city_pack_id', 'hk'));
    url.searchParams.set('city', field(payload, 'city', 'hong-kong'));
    url.searchParams.set('locale', field(payload, 'locale', 'en'));
    if (payload.district) url.searchParams.set('district', clean(payload.district));
    if (payload.record_id) {
      url.searchParams.set('record_id', clean(payload.record_id));
      url.searchParams.set('result_id', clean(payload.record_id));
    }
    if (payload.lead_category) url.searchParams.set('lead_category', clean(payload.lead_category));
    if (payload.daily_scene_id) url.searchParams.set('daily_scene_id', clean(payload.daily_scene_id));
    if (payload.service_lane) {
      url.searchParams.set('scene', clean(payload.service_lane));
      url.searchParams.set('service_lane', clean(payload.service_lane));
      if (rule.destination === 'gcitypartners') url.searchParams.set('scenario', clean(payload.service_lane));
    }
    if (payload.g_type === 'valuation') {
      url.searchParams.set('g_type', 'valuation');
      if (payload.valuation_type) url.searchParams.set('valuation_type', clean(payload.valuation_type));
      if (payload.property_purpose) url.searchParams.set('property_purpose', clean(payload.property_purpose));
      if (payload.value_band) url.searchParams.set('value_band', clean(payload.value_band));
      if (payload.risk_level) url.searchParams.set('risk_level', clean(payload.risk_level));
      if (payload.confidence_level) url.searchParams.set('confidence_level', clean(payload.confidence_level));
      if (payload.service_candidate_hint) url.searchParams.set('service_candidate_hint', clean(payload.service_candidate_hint));
      url.searchParams.set('repo_b_api_status', field(payload, 'repo_b_api_status', 'not_connected'));
      url.searchParams.set('avm_api_status', field(payload, 'avm_api_status', 'not_connected'));
    }
    if (payload.opportunity_status) url.searchParams.set('opportunity_status', clean(payload.opportunity_status));
    if (payload.visibility) url.searchParams.set('visibility', clean(payload.visibility));
    if (rule.launch_gate) url.searchParams.set('launch_gate', rule.launch_gate);
    if (rule.destination === 'gcitypartners') url.hash = 'gcp-brief-form';
    return external ? url.href : (url.pathname + url.search + url.hash);
  }

  function leadExitLabel(payload) {
    payload = payload && typeof payload === 'object' ? normalizeLeadRecord(payload, payload.storage_path || 'mission') : payload;
    return resolveExitRule(payload && payload.lead_type, payload && payload.next_destination).label;
  }

  function readList(storageKey, strict) {
    try {
      var raw = localStorage.getItem(storageKey);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object') {
        var items = parsed.items || parsed.cards || parsed.history;
        if (Array.isArray(items)) return items;
        return [parsed];
      }
      if (strict) throw new Error('saved_records_unavailable');
      return [];
    } catch (_) {
      if (strict) throw new Error('saved_records_unavailable');
      return [];
    }
  }

  function writeList(storageKey, list) {
    try {
      localStorage.setItem(storageKey, JSON.stringify(list || []));
      return true;
    } catch (error) {
      console.warn('[ResultStorage] localStorage list write failed:', storageKey, error);
      return false;
    }
  }

  function entryId(entry) {
    return entry && typeof entry === 'object'
      ? clean(entry.result_id || entry.record_id || entry.card_id || entry.id)
      : '';
  }

  function matchesEntryId(entry, id) {
    var target = clean(id);
    return !!(target && entryId(entry) === target);
  }

  function appendToList(storageKey, entry) {
    var id = entryId(entry);
    var list = readList(storageKey).filter(function (item) {
      return !matchesEntryId(item, id);
    });
    list.unshift(entry);
    return writeList(storageKey, pruneHistory(list));
  }

  function replaceInList(storageKey, id, entry) {
    var replaced = false;
    var list = readList(storageKey).map(function (item) {
      if (!matchesEntryId(item, id)) return item;
      replaced = true;
      return entry;
    });
    return replaced ? writeList(storageKey, list) : false;
  }

  function removeFromList(storageKey, id) {
    var list = readList(storageKey);
    var next = list.filter(function (entry) {
      return !matchesEntryId(entry, id);
    });
    if (next.length === list.length) return false;
    return writeList(storageKey, next);
  }

  function readSingle(storageKey) {
    try {
      var raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }

  function writeSingle(storageKey, entry) {
    try {
      localStorage.setItem(storageKey, JSON.stringify(entry));
      return true;
    } catch (error) {
      console.warn('[ResultStorage] localStorage write failed:', storageKey, error);
      return false;
    }
  }

  function removeSingle(storageKey) {
    try {
      localStorage.removeItem(storageKey);
      return true;
    } catch (_) {
      return false;
    }
  }

  function findResult(id) {
    var found = null;
    LIST_KEYS.some(function (storageKey) {
      found = readList(storageKey).find(function (entry) {
        return matchesEntryId(entry, id);
      }) || null;
      return !!found;
    });
    if (!found) {
      var last = readSingle(STORAGE_KEYS.lastResult);
      if (matchesEntryId(last, id)) found = last;
    }
    return found;
  }

  function persistExactResult(entry) {
    var id = entryId(entry);
    if (!id) return null;
    var stored = false;
    LIST_KEYS.forEach(function (storageKey) {
      stored = replaceInList(storageKey, id, entry) || stored;
    });
    var last = readSingle(STORAGE_KEYS.lastResult);
    if (matchesEntryId(last, id)) {
      stored = writeSingle(STORAGE_KEYS.lastResult, entry) || stored;
    }
    return stored ? entry : null;
  }

  function emit(name, detail) {
    try {
      window.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
    } catch (_) {}
  }

  var api = {
    version: VERSION,
    owner: 'assets/littleg-city-result-storage.js',
    leadExitRules: LEAD_EXIT_RULES,
    valuationMeta: VALUATION_META,
    normalizeLeadType: normalizeLeadType,
    normalizeNextDestination: normalizeNextDestination,
    resolveExitRule: resolveExitRule,
    buildLeadExitUrl: buildLeadExitUrl,
    leadExitLabel: leadExitLabel,
    normalizeLeadRecord: normalizeLeadRecord,
    isCityCard: isCityCard,

    saveResult: function (payload, path) {
      path = path || 'mission';
      var entry = normalizeLeadRecord(payload, path);
      var nativeState = window.LittleGWebSession && window.LittleGWebSession.getState ? window.LittleGWebSession.getState() : null;
      var profile = window.LittleGProfile && window.LittleGProfile.get ? window.LittleGProfile.get() : null;
      if (nativeState && nativeState.status === 'authenticated' && nativeState.profile && nativeState.profile.id) entry.owner_subject = nativeState.profile.id;
      else if (profile && profile.profile_id) entry.owner_profile_id = profile.profile_id;
      entry.saved_at = now();
      entry.expires_at = expiryIso(entry);
      entry.storage_path = path;

      var lastOk = writeSingle(STORAGE_KEYS.lastResult, entry);
      var historyOk = appendToList(STORAGE_KEYS.history, entry);
      if (path === 'generator') appendToList(STORAGE_KEYS.generatorHistory, entry);
      else if (path === 'drg') appendToList(STORAGE_KEYS.drgHistory, entry);
      if (!lastOk || !historyOk) return null;

      emit('littleg:city-mission:result-saved', { result: entry });
      return entry;
    },

    // Manual private-file handoff only. Do not carry foreign identity or permission data.
    importPlaybookJson: function (document) {
      if (!document || typeof document !== 'object' || Array.isArray(document) ||
          document.export_version !== 'lead_record_1.0') return null;
      var item = document.result;
      if (!item || typeof item !== 'object' || Array.isArray(item) ||
          clean(item.storage_path) !== 'generator' ||
          clean(item.lead_type) !== 'professional_project_playbook' ||
          clean(item.source) !== 'gcitypartners-project-playbook' ||
          clean(item.formal_submission_status) !== 'not_submitted' ||
          clean(item.visibility) !== 'private') return null;
      var handle = clean(item.generator_handle);
      var name = clean(item.generator_name);
      var question = clean(item.query);
      if (!/^[a-z0-9][a-z0-9-]{0,63}$/.test(handle) ||
          !name || name.length > 160 || !question || question.length > 5000 ||
          clean(item.playbook_stage).length > 400 ||
          clean(item.playbook_fit).length > 400 ||
          clean(item.playbook_output).length > 400) return null;
      return this.saveResult({
        id: uid(), lead_type: 'professional_project_playbook',
        source: 'gcitypartners-project-playbook',
        generator_handle: handle, generator_name: name,
        market_handle: clean(item.market_handle).slice(0, 80),
        market_name: clean(item.market_name).slice(0, 160),
        playbook_category: clean(item.playbook_category).slice(0, 80),
        playbook_stage: clean(item.playbook_stage), playbook_fit: clean(item.playbook_fit),
        playbook_output: clean(item.playbook_output), query: question,
        visibility: 'private', formal_submission_status: 'not_submitted',
        status: 'local_preview_ready'
      }, 'generator');
    },

    saveLocalizedResult: function (payload, path) {
      var source = payload && typeof payload === 'object' ? payload : {};
      var saved = this.saveResult(source, path || 'mission');
      if (!saved) return null;

      var sourceClues = localizedArray(source.clues);
      var localized = Object.assign({}, saved, {
        locale: clean(source.locale) || saved.locale,
        location: clean(source.place_label || source.location) || saved.location,
        place_label: clean(source.place_label) || clean(saved.place_label),
        scene_label: clean(source.scene_label) || clean(saved.scene_label),
        today_diary: clean(source.today_diary) || clean(saved.today_diary),
        memory_note: clean(source.memory_note) || clean(saved.memory_note),
        plus001_direction: clean(source.plus001_direction) || clean(saved.plus001_direction),
        informal_summary: clean(source.informal_summary) || clean(saved.informal_summary),
        next_step_service_lane: clean(source.next_step_service_lane) || clean(saved.next_step_service_lane),
        share_text: clean(source.share_text) || clean(saved.share_text),
        clues: sourceClues.length ? sourceClues : saved.clues,
        visibility: saved.visibility || 'private',
        formal_submission_status: saved.formal_submission_status || 'not_submitted'
      });

      var persisted = persistExactResult(localized);
      if (!persisted) return null;
      emit('littleg:city-mission:result-updated', { result: localized, reason: 'localized-save' });
      return localized;
    },

    removeResult: function (id) {
      id = clean(id);
      if (!id) return false;
      var removed = false;
      LIST_KEYS.forEach(function (storageKey) {
        removed = removeFromList(storageKey, id) || removed;
      });
      var last = readSingle(STORAGE_KEYS.lastResult);
      if (matchesEntryId(last, id)) {
        removed = removeSingle(STORAGE_KEYS.lastResult) || removed;
      }
      if (removed) emit('littleg:city-mission:result-removed', { result_id: id });
      return removed;
    },

    getResult: function (id) {
      id = clean(id);
      if (!id) return null;
      var entry = findResult(id);
      if (!entry) return null;
      if (isExpired(entry)) {
        this.removeResult(id);
        return null;
      }
      return normalizeLeadRecord(entry, entry.storage_path || 'mission');
    },

    getLastResult: function () {
      var entry = readSingle(STORAGE_KEYS.lastResult);
      if (!entry) return null;
      if (isExpired(entry)) {
        this.removeResult(entryId(entry));
        return null;
      }
      return normalizeLeadRecord(entry, entry.storage_path || 'mission');
    },

    exportResultJson: function (id) {
      var entry = this.getResult(id);
      if (!entry) return null;
      var playbook = entry.storage_path === 'generator' &&
        entry.lead_type === 'professional_project_playbook' &&
        entry.source === 'gcitypartners-project-playbook';
      // Portable Playbook files contain project details only, never user/session identifiers.
      var portable = playbook ? {
        record_id: entry.record_id,
        storage_path: 'generator',
        lead_type: 'professional_project_playbook',
        source: 'gcitypartners-project-playbook',
        generator_handle: clean(entry.generator_handle),
        generator_name: clean(entry.generator_name),
        market_handle: clean(entry.market_handle),
        market_name: clean(entry.market_name),
        playbook_category: clean(entry.playbook_category),
        playbook_stage: clean(entry.playbook_stage),
        playbook_fit: clean(entry.playbook_fit),
        playbook_output: clean(entry.playbook_output),
        query: clean(entry.query),
        visibility: 'private',
        formal_submission_status: 'not_submitted'
      } : entry;
      return {
        export_version: entry.g_type === 'valuation' ? 'valuation_snapshot_1.0' : 'lead_record_1.0',
        exported_at: now(),
        schema_version: entry.schema_version || 'littleg_lead_record_v1',
        result: portable,
        boundary: entry.g_type === 'valuation'
          ? 'Local LittleG valuation snapshot only. Not a formal valuation, lender valuation, mortgage approval, tax opinion, survey report, professional report or Repo B archive record.'
          : 'Local LittleG clue record only. Not a formal submission, valuation, survey, statutory filing, government report or public tender record.',
        next_destination: entry.next_destination || 'cards',
        lead_exit_url: buildLeadExitUrl(entry)
      };
    },

    listAll: function (options) {
      var strict = options && options.strict === true;
      return readList(STORAGE_KEYS.history, strict)
        .filter(function (entry) {
          // CV reads must never invent an ID or saved time for a malformed legacy row.
          return !strict || (entry && clean(entry.record_id || entry.result_id || entry.card_id || entry.cardId || entry.id)
            && Number.isFinite(Date.parse(entry.saved_at || entry.confirmed_at)));
        })
        .map(function (entry) {
          return normalizeLeadRecord(entry, entry && entry.storage_path ? entry.storage_path : 'mission');
        })
        .filter(function (entry) {
          return !isExpired(entry);
        })
        .sort(function (a, b) {
          return new Date(b.saved_at || b.created_at) - new Date(a.saved_at || a.created_at);
        });
    },

    clearAll: function () {
      Object.keys(STORAGE_KEYS).forEach(function (name) {
        removeSingle(STORAGE_KEYS[name]);
      });
      emit('littleg:city-mission:results-cleared', { scope: 'all-local-results' });
      return true;
    }
  };

  window.LittleGResultStorage = Object.freeze(api);

  function handleGeneratorSaveRequest(event) {
    var detail = event && event.detail ? event.detail : {};
    if (detail.saved || detail.saved_result) return;
    var payload = detail.result || detail.payload;
    if (!payload || typeof payload !== 'object') return;
    var saved = api.saveResult(payload, detail.path || 'generator');
    try {
      window.dispatchEvent(new CustomEvent('littleg:generator-preview:saved', {
        detail: {
          result: saved || payload,
          saved: !!saved,
          path: detail.path || 'generator',
          source: 'littleg-city-result-storage',
          requested_by: detail.owner || detail.source || ''
        }
      }));
    } catch (_) {}
  }

  window.addEventListener('littleg:generator-preview:save-requested', handleGeneratorSaveRequest);
  console.log('[ResultStorage] Loaded.', VERSION, Object.keys(LEAD_EXIT_RULES).join(', '));
})();
